import {
	Button,
	Disclosure,
	DisclosurePanel,
	DisclosureTitle,
	StatusLight,
	Text,
} from '@react-spectrum/s2';
import ChartTrend from '@react-spectrum/s2/icons/ChartTrend';
import Location from '@react-spectrum/s2/icons/Location';
import People from '@react-spectrum/s2/icons/People';
import Promote from '@react-spectrum/s2/icons/Promote';
import { useState, type ReactNode } from 'react';

import {
	ClampedProse,
	StepFieldEdit,
} from '@/spaces/chat/panels/StepFieldEdit';
import { cn } from '@/utils/cn';

import type {
	StrategyCampaignProposal,
	StrategyDefinition,
	StrategyMetric,
	Strategy,
} from '../../../../bridge/types/strategy.ts';

import { CampaignBuiltWork } from './CampaignBuiltWork';
import {
	ArtifactChip,
	BasisNote,
	GlossedText,
	IconMedallion,
	MetricGauge,
	MetricProgressBar,
	ProvChips,
	SectionLabel,
} from './StrategyBits';
import {
	campaignBuildState,
	formatMetricValue,
	isActivatableReadiness,
	isOwnerRun,
	isOwnerStep,
	metricCheckpoints,
	normalizeWhere,
	prereqActions,
	prereqProse,
	type CampaignBuildState,
} from './strategyFormat';
import type { MaterializeCampaignReply } from './useStrategyActions';
import type { StrategyPlanView } from './useStrategyPlanView';

function MetricCard({
	m,
	variant,
	showProv,
	metricIndex,
	definitions,
	glossSites,
	currentValue,
}: {
	m: StrategyMetric;
	variant: 'primary' | 'supporting';
	showProv: boolean;
	metricIndex: number;
	definitions?: StrategyDefinition[];
	glossSites: Map<string, string>;
	// The north-star's latest measured value. Primary-only: supporting metrics are
	// visibility, and their slim bar is documented as baseline→target, not live.
	currentValue?: number | null;
}) {
	const hasBaseline =
		typeof m.metric.baseline === 'number' && m.metric.baseline > 0;
	const isManual = !('key' in m.metric.tracking);
	const readout = formatMetricValue(m.metric.target, m.metric.unit);
	const unitTrim = (m.metric.unit ?? '').trim();
	const gaugeNumber =
		unitTrim === '%'
			? `${m.metric.target.toLocaleString()}%`
			: m.metric.target.toLocaleString();
	const checkpoints = metricCheckpoints(m.metric.target, m.metric.baseline);

	const delta = hasBaseline && (
		<span className="text-sm font-normal text-(--text-muted)">
			up from {formatMetricValue(m.metric.baseline!, m.metric.unit)}
		</span>
	);
	const manualNote = isManual && (
		<span className="text-sm font-normal text-(--text-muted)">
			(Track manually)
		</span>
	);
	const purpose = m.purpose && (
		<p className="text-sm text-(--text-secondary)">
			<span className="font-semibold text-(--text-primary)">
				Why it matters:
			</span>{' '}
			<GlossedText
				where={`metric[${metricIndex}].purpose`}
				text={m.purpose}
				definitions={definitions}
				sites={glossSites}
			/>
		</p>
	);
	const prov = (
		<div>
			{showProv && <BasisNote basis={m.metric.basis} />}
			<ProvChips provenance={m.provenance} show={showProv} />
		</div>
	);

	if (variant === 'supporting') {
		return (
			<div
				className="glass-card-sm border-beacon-stroke space-y-2 border-l-2 p-4"
			>
				<p
					className="text-xs font-semibold tracking-wide text-(--text-muted)
						uppercase"
				>
					Supporting signal
				</p>
				<p className="text-sm font-semibold text-(--text-primary)">
					{m.metric.name}
				</p>
				<p
					className="flex flex-wrap items-baseline gap-x-2 text-lg leading-none
						font-semibold text-(--text-secondary)"
				>
					<span>{readout}</span>
					{delta}
					{manualNote}
				</p>
				{hasBaseline && (
					<MetricProgressBar
						baseline={m.metric.baseline!}
						target={m.metric.target}
					/>
				)}
				{purpose}
				{prov}
			</div>
		);
	}

	return (
		<div className="glass-card space-y-4 p-6 shadow-[var(--shadow-card)]">
			<div className="flex items-center gap-6">
				<MetricGauge
					target={m.metric.target}
					baseline={m.metric.baseline}
					current={variant === 'primary' ? currentValue : undefined}
				>
					<div
						className="flex items-center justify-center px-2 text-center
							leading-none"
					>
						<span className="text-2xl font-bold text-(--text-primary)">
							{gaugeNumber}
						</span>
					</div>
				</MetricGauge>
				<div className="min-w-0 flex-1 space-y-1.5">
					<p
						className="text-xs font-semibold tracking-wide text-(--text-muted)
							uppercase"
					>
						North-star
					</p>
					<p className="text-xl leading-tight font-bold text-(--text-primary)">
						{m.metric.name}
					</p>
					<p
						className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium
							text-(--text-secondary)"
					>
						<span>{readout}</span>
						{delta}
						{manualNote}
					</p>
				</div>
			</div>
			{checkpoints.length > 0 && (
				<div className="space-y-2">
					<p
						className="text-xs font-semibold tracking-wide text-(--text-muted)
							uppercase"
					>
						Checkpoints
					</p>
					<div className="flex w-full items-center gap-2">
						{checkpoints.map((cp, i) => (
							<div key={cp.value} className="contents">
								{i > 0 && (
									<span aria-hidden className="bg-beacon-stroke h-px flex-1" />
								)}
								<div className="flex shrink-0 items-center gap-1.5">
									<span
										aria-hidden
										className={cn(
											'border-beacon-canary size-2 shrink-0 rounded-full border',
											cp.reached ? 'bg-beacon-canary' : 'bg-white',
										)}
									/>
									<span className="text-sm font-medium text-(--text-secondary)">
										{cp.value.toLocaleString()}
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
			{purpose}
			{prov}
		</div>
	);
}

/**
 * How much of a campaign a surface shows.
 *
 * `full` is the review panel: the owner is deciding, so they get everything —
 * what was built, and the plan behind it. `summary` is the dock's detail view,
 * where a campaign is one row in a list of them; reading a campaign in depth
 * belongs to the panel, and having both surfaces render the same long body meant
 * maintaining one screen's worth of decisions in two places.
 */
export type CampaignsVariant = 'full' | 'summary';

/** Build one campaign. Resolves to the route's reply, whose `status` says which
 * terminal state it landed on — see MaterializeCampaignReply. */
export type CampaignBuildRequest = (
	strategyId: string,
	campaignId: string,
) => Promise<MaterializeCampaignReply>;

/**
 * How a campaign that has already been through the builder reads.
 *
 * One renderer for the four settled states, so the same fact cannot be worded
 * differently on the dock and in the review panel. `built` and `live` are
 * deliberately different sentences: building produces a Process sitting in
 * `proposed` with nothing running, and only the owner's yes starts it. Calling a
 * built campaign "Activated" is the bug this component exists to not have.
 */
function BuildStatus({
	state,
	ownerRun,
	variant,
	onViewSetup,
}: {
	state: CampaignBuildState;
	ownerRun: boolean;
	variant: CampaignsVariant;
	onViewSetup: (processId: string) => void;
}) {
	if (state.kind === 'building') {
		return (
			<div className="flex flex-col items-start gap-1">
				<StatusLight variant="notice" size="S">
					Setting up…
				</StatusLight>
				{/* The reassurance belongs where the content is about to appear. The dock
				    summary is a list of statuses, so there the light says it all. */}
				{variant === 'full' && (
					<p className="text-sm text-(--text-muted)">
						This campaign is being set up — it&apos;ll appear here shortly.
					</p>
				)}
			</div>
		);
	}

	if (state.kind === 'failed') {
		// `state.message` is a truncated backend exception and never reaches the
		// screen. The owner needs to know the outcome and what happens next, and the
		// next open retries automatically — so that is the whole sentence.
		return (
			<p
				data-testid="strategy-campaign-build-failed"
				className="text-sm text-(--text-muted)"
			>
				We couldn&apos;t build this campaign. It&apos;ll retry next time you
				open this strategy.
			</p>
		);
	}

	if (state.kind === 'built' || state.kind === 'live') {
		const live = state.kind === 'live';
		const { processId } = state;
		return (
			<div className="flex items-center gap-3">
				<StatusLight variant={live ? 'positive' : 'informative'} size="S">
					{live
						? ownerRun
							? 'Ready for you'
							: 'Activated'
						: 'Ready to review'}
				</StatusLight>
				<Button
					variant="secondary"
					size="S"
					onPress={() => onViewSetup(processId)}
				>
					<Text>{ownerRun ? 'View my part' : 'View setup'}</Text>
				</Button>
			</div>
		);
	}

	return null;
}

/**
 * Lets the owner preview what a campaign would build before committing.
 *
 * Rendered only while the campaign is not built AND the caller passed an
 * `onActivate`. The route it calls materializes a Process for the owner to read;
 * nothing starts until they say yes. Previewing is optional — un-previewed
 * campaigns are materialized at strategy activation time.
 */
function BuildControl({
	strategyId,
	campaign,
	ownerRun,
	variant,
	onActivate,
	onActivationConflict,
	onViewSetup,
}: {
	strategyId: string;
	campaign: StrategyCampaignProposal;
	ownerRun: boolean;
	variant: CampaignsVariant;
	onActivate: CampaignBuildRequest;
	onActivationConflict?: () => Promise<void>;
	onViewSetup: (processId: string) => void;
}) {
	// Local state describes THIS click and nothing else — the optimistic result the
	// owner should see before any push lands. It needs no reconciliation effect:
	// once a push moves the campaign off `not_built`, the parent stops rendering
	// this component at all, so the server's account replaces the optimistic one by
	// construction rather than by two derivations agreeing.
	const [pending, setPending] = useState(false);
	const [result, setResult] = useState<CampaignBuildState | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	if (result) {
		return (
			<BuildStatus
				state={result}
				ownerRun={ownerRun}
				variant={variant}
				onViewSetup={onViewSetup}
			/>
		);
	}

	return (
		<div className="flex flex-col items-start gap-1">
			<Button
				variant="accent"
				size="S"
				isPending={pending}
				isDisabled={pending}
				onPress={async () => {
					if (pending) return;
					setError(null);
					setNotice(null);
					setPending(true);
					try {
						const reply = await onActivate(strategyId, campaign.id);
						// Read through the SAME derivation a server push goes through, so this
						// click and the refresh that follows it cannot describe the campaign
						// differently. The reply carries the status precisely because building
						// and going live are different outcomes.
						if (reply.status === 'superseded') {
							// The plan moved under the build, so the route threw the content
							// away rather than store work made from the old plan. Say that, and
							// leave the control pressable: building again from the new plan is
							// exactly the right next act.
							setNotice(
								'Your plan changed while this was building, so we started over. ' +
									'Preview it again to see the new version.',
							);
							return;
						}
						const derived = campaignBuildState(reply);
						// One fact the derivation cannot see: the route ANSWERED, with a process
						// id. On a stored campaign a process id and no status means a cleared
						// marker and must not read as built — but here it is proof the build just
						// happened, so a `status`-less reply from an older backend floors at
						// `built` instead of erasing the control it was pressed on.
						setResult(
							derived.kind === 'not_built' && reply.process_id
								? { kind: 'built', processId: reply.process_id }
								: derived,
						);
					} catch (err) {
						const conflict = (err as { status?: number })?.status === 409;
						if (conflict) {
							// Someone (or a previous open) already holds the build claim, so the
							// truthful reading of this click is "it is being set up".
							setResult({ kind: 'building' });
							void onActivationConflict?.();
						} else {
							setError(
								(err as Error)?.message ??
									"We couldn't build this campaign. Please try again.",
							);
						}
					} finally {
						setPending(false);
					}
				}}
			>
				<Text>Preview this campaign</Text>
			</Button>
			{error && <p className="text-sm text-red-600">{error}</p>}
			{/* Not red. A superseded build is a correct outcome of the owner's own edit. */}
			{notice && (
				<p
					data-testid="strategy-campaign-build-superseded"
					className="text-sm text-(--text-secondary)"
				>
					{notice}
				</p>
			)}
		</div>
	);
}

/**
 * Reveals the planner's steps behind a disclosure the owner has to ask for.
 *
 * A plain button and not an S2 `Disclosure`: the detail view's suite mocks
 * `Disclosure` to a `campaign-card` div, so nesting one makes every
 * `getByTestId('campaign-card')` throw on multiple matches. The steps are also
 * secondary here by design — once real content exists, the plan is context, not
 * the thing being judged.
 */
function OriginalPlanToggle({ children }: { children: ReactNode }) {
	const [open, setOpen] = useState(false);
	return (
		<div className="space-y-2">
			<button
				type="button"
				data-testid="strategy-campaign-plan-toggle"
				onClick={() => setOpen((o) => !o)}
				// NOT uppercase. Uppercase-muted is this file's SECTION LABEL treatment, so the
				// toggle read as another heading rather than as something to press.
				className="min-h-6 cursor-pointer text-xs font-semibold
					text-(--text-secondary) underline underline-offset-2
					hover:text-(--text-primary)"
			>
				{open ? 'Hide the original plan' : 'Show the original plan'}
			</button>
			{open && children}
		</div>
	);
}

/** The planner's steps for one campaign. Extracted unchanged so the plan reads
 * identically whether it is the main event (nothing built yet) or the context
 * behind what was built. */
function PlanSteps({
	campaign,
	campaignIndex,
	ownerRun,
	definitions,
	glossSites,
}: {
	campaign: StrategyCampaignProposal;
	campaignIndex: number;
	ownerRun: boolean;
	definitions?: StrategyDefinition[];
	glossSites: Map<string, string>;
}) {
	return (
		<ol className="space-y-3">
			{campaign.steps!.map((s, j) => (
				<li
					key={j}
					data-testid="strategy-campaign-step"
					data-run-by={isOwnerStep(s) ? 'owner' : 'bridgeway'}
					className="border-beacon-stroke rounded-lg border bg-white/50 p-4"
				>
					<div className="min-w-0 space-y-3">
						<div className="flex gap-3">
							<span
								aria-hidden
								className="mt-px shrink-0 text-sm font-semibold
									text-(--text-muted) tabular-nums"
							>
								{j + 1}
							</span>
							<p
								className="min-w-0 flex-1 text-sm leading-relaxed font-medium
									text-(--text-primary)"
							>
								<GlossedText
									where={`campaign[${campaignIndex}].step[${j}].description`}
									text={s.description}
									definitions={definitions}
									sites={glossSites}
								/>
							</p>
							{isOwnerStep(s) && !ownerRun && (
								<StatusLight variant="seafoam" size="S">
									You run this
								</StatusLight>
							)}
						</div>
						{(s.artifacts?.length ?? 0) > 0 && (
							<div className="flex flex-wrap gap-1.5 pl-7">
								{s.artifacts!.map((a, k) => (
									<ArtifactChip
										key={k}
										artifactType={
											a.artifactType ?? (a as { type?: string }).type
										}
									/>
								))}
							</div>
						)}
						{(s.execution?.length ?? 0) > 0 && (
							<ol className="space-y-3 pl-7">
								{s.execution!.map((e, k) => {
									const needs = e.needs?.trim();
									const actor =
										e.who && e.who.toLowerCase() !== 'you' ? e.who : null;
									return (
										<li key={k} className="flex gap-2.5">
											<span
												aria-hidden
												className="mt-px shrink-0 text-xs text-(--text-muted)
													tabular-nums"
											>
												{k + 1}.
											</span>
											<div className="min-w-0 flex-1 space-y-1.5">
												<p
													className="text-sm leading-relaxed
														text-(--text-secondary)"
												>
													{e.action}
												</p>
												{actor && (
													<p className="text-xs text-(--text-muted) italic">
														{actor}
													</p>
												)}
												{needs && (
													<span
														className="bg-beacon-light-gray inline-flex
															items-center gap-1.5 rounded-full px-2.5 py-1
															text-xs text-(--text-muted)"
													>
														<span
															className="text-[10px] font-medium tracking-wide
																text-(--text-muted) uppercase"
														>
															Needs
														</span>
														<span className="text-(--text-secondary)">
															{needs}
														</span>
													</span>
												)}
											</div>
										</li>
									);
								})}
							</ol>
						)}
					</div>
				</li>
			))}
		</ol>
	);
}

function CampaignCard({
	strategyId,
	campaign,
	campaignIndex,
	onEditCampaign,
	defaultExpanded,
	collapsible,
	showProv,
	variant,
	onActivate,
	onActivationConflict,
	onViewSetup,
	definitions,
	glossSites,
}: {
	strategyId: string;
	campaign: StrategyCampaignProposal;
	campaignIndex: number;
	defaultExpanded: boolean;
	/** The dock stacks many campaigns, so it collapses them. A surface showing ONE campaign
	 *  passes false: a control that collapses the only thing on screen offers nothing. */
	collapsible: boolean;
	showProv: boolean;
	variant: CampaignsVariant;
	/** Absent means this surface is read-only. That is the whole signal — no
	 * separate boolean that a missing callback could contradict. */
	onActivate?: CampaignBuildRequest;
	onActivationConflict?: () => Promise<void>;
	onViewSetup: (processId: string) => void;
	definitions?: StrategyDefinition[];
	glossSites: Map<string, string>;
	onEditCampaign?: (
		campaignId: string,
		edit: Partial<
			Record<'title' | 'aim' | 'what_you_do' | 'report_back', string>
		>,
	) => Promise<void>;
}) {
	const ownerRun = isOwnerRun(campaign);
	const mixedLanes = !ownerRun && (campaign.steps ?? []).some(isOwnerStep);
	const stepsLabel = ownerRun
		? "What you'll do"
		: mixedLanes
			? 'How it runs, in order'
			: 'How it runs';
	const payoff = (campaign.contributes ?? [])[0]?.how?.trim();
	const canActivate = isActivatableReadiness(campaign.readiness);
	// Derived ONCE per card, and passed down. Two components deriving this
	// independently is what let a mid-build campaign show a fresh Build button.
	const buildState = campaignBuildState(campaign);
	const hasSteps = (campaign.steps?.length ?? 0) > 0;
	const full = variant === 'full';

	const planSteps = hasSteps ? (
		<PlanSteps
			campaign={campaign}
			campaignIndex={campaignIndex}
			ownerRun={ownerRun}
			definitions={definitions}
			glossSites={glossSites}
		/>
	) : null;

	// What the body leads with. Once a campaign is built, the specific content is
	// what the owner should be judging, so it leads and the planner's steps move
	// behind a toggle — they say WHY, which the artifacts do not, so they are kept
	// rather than dropped. Before anything is built, the plan IS the content.
	const bodySection =
		buildState.kind === 'built' || buildState.kind === 'live' ? (
			<>
				{/* No "What we built" label over it. Each section inside names itself — "What we'll
				    publish", "Your part", "Runs continuously" — so an outer heading stacked a
				    second uppercase label directly above the first and named nothing new. */}
				<CampaignBuiltWork
					processId={buildState.processId}
					isLive={buildState.kind === 'live'}
				/>
				{planSteps && <OriginalPlanToggle>{planSteps}</OriginalPlanToggle>}
			</>
		) : planSteps ? (
			<div className="space-y-2">
				<SectionLabel>{stepsLabel}</SectionLabel>
				{planSteps}
			</div>
		) : null;

	const statusRow =
		buildState.kind === 'not_built' ? (
			// A build control only where building is on offer. The review panel passes
			// no `onActivate` because opening it already builds every campaign, so a
			// button there would offer work that is already underway.
			canActivate && onActivate ? (
				<BuildControl
					strategyId={strategyId}
					campaign={campaign}
					ownerRun={ownerRun}
					variant={variant}
					onActivate={onActivate}
					onActivationConflict={onActivationConflict}
					onViewSetup={onViewSetup}
				/>
			) : null
		) : (
			<BuildStatus
				state={buildState}
				ownerRun={ownerRun}
				variant={variant}
				onViewSetup={onViewSetup}
			/>
		);

	const header = (
		<span className="flex min-w-0 flex-1 items-center gap-3">
			<IconMedallion icon={Promote} />
			<span
				className="min-w-0 flex-1 truncate font-medium text-(--text-primary)"
			>
				{campaign.title}
			</span>
			{campaign.readiness === 'requires_setup' && (
				<StatusLight variant="notice" size="S">
					{prereqActions(campaign.prerequisites).join(' · ') ||
						'Beacon runs after setup'}
				</StatusLight>
			)}
			{isOwnerRun(campaign) && (
				<StatusLight variant="seafoam" size="S">
					You run this
				</StatusLight>
			)}
		</span>
	);
	const body = (
		<div className="space-y-5 px-1 pt-1 pb-1">
			{/* design.md's "Expandable text rows in a card" recipe, which that file writes for
		    this surface by name. Each prose line is one ROW of a hairline-divided list:
		    a title line, a body clamped to two lines, and its own measured More toggle.
		    What it replaced, and why:
		     - a grey `bg-beacon-light-gray/70` box around the move. The recipe forbids a
		       per-row tint, because tinting a glass card's own body inverts figure and
		       ground.
		     - three `border-beacon-stroke border-l-2` blocks. A 2px vertical rule appears
		       NOWHERE in design.md, at double the 1px hairline weight, in a direction the
		       system uses only horizontally, so each block read as a fourth surface.
		     - all four lines unclamped at once, which is what ran the card past the fold
		       in a panel roughly 590px wide. */}
			<ul>
				{CAMPAIGN_PROSE_ROWS.map(({ key, label, openLabel, editKey }) => {
					const value = campaign[key];
					if (!value) return null;
					// The name row exists ONLY to rename the campaign. On a read-only surface the
					// header already states the name, so the row would repeat it with nothing to
					// press.
					if (key === 'title' && !onEditCampaign) return null;
					// The move LEADS. It is what the campaign is, so it reads as the card's own
					// sentence. Blocks of equal weight give an owner nothing to land on, which is
					// what kept this card unreadable however it was spaced.
					const lead = key === 'aim';
					const body = (
						<ClampedProse
							text={value}
							// Measured, so a toggle appears only on a row that really overflows. At two
							// lines almost every row overflowed and grew a "More", and four of those
							// read as debris down the card. Three fits most of them outright.
							lines={lead ? 4 : 3}
							// `editKey`, so one row carries ONE spelling. `where` below keeps the
							// document's own field name, because it addresses the plan, not the route.
							testId={`strategy-campaign-${editKey}-${campaign.id}`}
							className={
								lead
									? 'text-base leading-relaxed text-(--text-primary)'
									: 'text-sm leading-relaxed text-(--text-secondary)'
							}
						>
							<GlossedText
								where={`campaign[${campaignIndex}].${key}`}
								text={value}
								definitions={definitions}
								sites={glossSites}
							/>
						</ClampedProse>
					);
					return (
						<li
							key={key}
							className="border-t border-black/[0.06] py-3 first:border-t-0
								first:pt-0"
						>
							{onEditCampaign ? (
								<StepFieldEdit
									kind="text"
									label={label}
									testId={`strategy-campaign-${editKey}-${campaign.id}`}
									openLabel={openLabel}
									value={value}
									onSave={(next) =>
										onEditCampaign(campaign.id, { [editKey]: next })
									}
								>
									{body}
								</StepFieldEdit>
							) : (
								<>
									<p
										className="text-xs font-semibold tracking-wide
											text-(--text-muted) uppercase"
									>
										{label}
									</p>
									{body}
								</>
							)}
						</li>
					);
				})}
				{full && prereqProse(campaign.prerequisites).length > 0 && (
					// Read-only on purpose. A prerequisite drives how Bridgeway builds the
					// campaign, so it is not the owner's to reword from here.
					<li
						className="border-t border-black/[0.06] py-3 first:border-t-0
							first:pt-0"
					>
						<p
							className="text-xs font-semibold tracking-wide text-(--text-muted)
								uppercase"
						>
							Needed first
						</p>
						<ClampedProse
							text={prereqProse(campaign.prerequisites).join(' ')}
							lines={3}
							testId={`strategy-campaign-prereq-${campaign.id}`}
							className="text-sm leading-relaxed text-(--text-secondary)"
						/>
					</li>
				)}
			</ul>

			{full && bodySection}

			{full && payoff && (
				<div
					className="border-beacon-stroke bg-beacon-ivory flex items-start gap-3
						rounded-lg border px-4 py-3.5"
				>
					<span
						aria-hidden
						className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-md
							bg-white text-(--text-muted) [--iconPrimary:currentColor]
							[&_svg]:size-4"
					>
						<ChartTrend />
					</span>
					<p className="text-sm leading-relaxed text-(--text-secondary)">
						<span className="font-semibold text-(--text-primary)">
							Why it helps
						</span>{' '}
						<GlossedText
							where={`campaign[${campaignIndex}].contributes[0].how`}
							text={payoff}
							definitions={definitions}
							sites={glossSites}
						/>
					</p>
				</div>
			)}

			{full && <ProvChips provenance={campaign.provenance} show={showProv} />}

			{statusRow}
		</div>
	);

	return (
		// The card frame lives on this wrapper, not on the Disclosure. S2 styles a
		// Disclosure as an accordion row — 1px top/bottom hairlines and explicitly
		// zeroed side borders — from its own cascade layer, which sorts after
		// Tailwind's utilities. Put `glass-card-sm` on the Disclosure and it loses
		// every border longhand, so the left and right sides vanish while the
		// surviving border-radius tapers each corner into nothing.
		<div
			data-testid="strategy-campaign-card"
			data-lanes={mixedLanes ? 'mixed' : ownerRun ? 'owner' : 'bridgeway'}
			className="glass-card-sm glass-card-hover"
		>
			{collapsible ? (
				<Disclosure
					defaultExpanded={defaultExpanded}
					styles={undefined}
					UNSAFE_className="campaign-card-disclosure"
				>
					<DisclosureTitle>{header}</DisclosureTitle>
					<DisclosurePanel>{body}</DisclosurePanel>
				</Disclosure>
			) : (
				// One campaign on screen. A control that collapses the only thing there
				// offers the owner nothing, so the card is simply open.
				<div className="space-y-4 p-4">
					{header}
					{body}
				</div>
			)}
		</div>
	);
}

// --- Section components shared by StrategyDetailView and StrategyReviewPanel ---

// The callbacks the campaign cards need. It used to carry an `activations` record too,
// keyed by campaign id; that state lives on each campaign now.
export interface CampaignActivationHandlers {
	strategyId: string;
	/** Optional: a surface that offers no building simply omits it. There used to be
	 * a default here that THREW when pressed, which meant a read-only surface still
	 * rendered a button and only admitted it could do nothing after the click. */
	onActivate?: CampaignBuildRequest;
	onActivationConflict?: () => Promise<void>;
	onViewSetup: (processId: string) => void;
}

// Every campaign line the owner reads, in reading order, with the `set_campaign` field that
// changes it. One table rather than one callback per field, because the op carries all of them.
// The campaign-level prose the CARD renders. `whatYouDo` and `reportBack` are deliberately
// absent: the steps already say who does each part and what they report, per step, and "Your
// part" lists the owner's asks specifically once the campaign is built. A campaign-level summary
// of both restated the steps in vaguer words directly above them.
//
// Both fields stay on the record and stay editable through the `set_campaign` op, so the
// assistant can still change them and the dock's other readers are untouched. This is a
// rendering decision, not a data one.
const CAMPAIGN_PROSE_ROWS = [
	{
		key: 'aim',
		editKey: 'aim',
		label: 'The move',
		openLabel: 'Change the move',
	},
	// The name repeats the card header on purpose. The header states which campaign this is; this
	// row is the control that renames it. Without the row the name is the one plan line an owner
	// can read but cannot change.
	{
		key: 'title',
		editKey: 'title',
		label: 'Name',
		openLabel: 'Rename this campaign',
	},
] as const;

export function CampaignsSection({
	run,
	view,
	showProv,
	variant = 'full',
	campaignActivation,
	campaignIds,
	onEditCampaign,
}: {
	run: Strategy;
	view: StrategyPlanView;
	showProv: boolean;
	variant?: CampaignsVariant;
	campaignActivation?: CampaignActivationHandlers;
	/** Show only these campaigns, in plan order. Absent means every campaign, which is what
	 *  the dock detail view and a running strategy both pass. */
	campaignIds?: string[];
	/** Present = the aim is editable here. Absent IS the read-only signal, the same convention
	 *  `onActivate` already uses in this file: a defaulted callback that threw once let a
	 *  read-only surface draw a control that admitted it could do nothing only after the click. */
	onEditCampaign?: (
		campaignId: string,
		edit: Partial<
			Record<'title' | 'aim' | 'what_you_do' | 'report_back', string>
		>,
	) => Promise<void>;
}) {
	const { campaigns, definitions, glossSites } = view;

	if (campaigns.length === 0) {
		return (
			<p className="text-sm text-(--text-muted)">
				No campaigns in this strategy yet.
			</p>
		);
	}

	return (
		<div data-testid="strategy-campaign-list" className="flex flex-col gap-4">
			{campaigns.map((c, idx) => {
				// Skip INSIDE the map, never filter the array. `idx` is the campaign's plan-order
				// position and it builds every provenance and gloss address below, so a filtered
				// array would make campaign 3 address campaign 0's glosses. A missing gloss key
				// renders plain text, so that fault would be silent.
				if (campaignIds && !campaignIds.includes(c.id)) return null;
				return (
					<CampaignCard
						key={c.id}
						strategyId={campaignActivation?.strategyId ?? run.id}
						campaign={c}
						campaignIndex={idx}
						// A guided step shows exactly one campaign, so that campaign is the whole
						// point of the step and must be open. Without this, every campaign after
						// the first renders as one collapsed row carrying only its title.
						defaultExpanded={campaignIds ? true : idx === 0}
						collapsible={!campaignIds}
						showProv={showProv}
						variant={variant}
						onActivate={campaignActivation?.onActivate}
						onActivationConflict={campaignActivation?.onActivationConflict}
						onViewSetup={campaignActivation?.onViewSetup ?? (() => undefined)}
						definitions={definitions}
						glossSites={glossSites}
						onEditCampaign={onEditCampaign}
					/>
				);
			})}
		</div>
	);
}

export function AudienceSection({
	view,
	showProv,
}: {
	view: StrategyPlanView;
	showProv: boolean;
}) {
	const { strategy } = view;

	if (!strategy?.audience?.who) {
		return (
			<p className="text-sm text-(--text-muted)">
				No audience defined for this strategy yet.
			</p>
		);
	}

	return (
		<>
			<div className="space-y-3">
				<div className="glass-card-sm flex items-start gap-3 p-4">
					<IconMedallion icon={People} />
					<div className="min-w-0 flex-1 space-y-1">
						<p className="text-xs font-semibold text-(--text-muted)">
							Who this is for
						</p>
						<p
							className="max-w-[60ch] text-sm leading-relaxed
								text-(--text-primary)"
						>
							{strategy.audience.who}
						</p>
					</div>
				</div>
				{normalizeWhere(strategy.audience.where).length > 0 && (
					<div className="glass-card-sm flex items-start gap-3 p-4">
						<IconMedallion icon={Location} />
						<div className="min-w-0 flex-1 space-y-1">
							<p className="text-xs font-semibold text-(--text-muted)">
								Where they gather
							</p>
							<ul className="divide-y divide-(--glass-border)">
								{normalizeWhere(strategy.audience.where).map((l, i) => (
									<li key={i} className="space-y-0.5 py-3 first:pt-1 last:pb-0">
										<p
											className="text-sm leading-snug font-semibold
												text-(--text-primary)"
										>
											{l.place}
										</p>
										{l.note && (
											<p
												className="max-w-[64ch] text-sm leading-relaxed
													text-(--text-muted)"
											>
												{l.note}
											</p>
										)}
									</li>
								))}
							</ul>
						</div>
					</div>
				)}
			</div>
			{showProv && (
				<div className="space-y-1 text-sm text-(--text-muted)">
					{strategy.audience.whyThem && <p>{strategy.audience.whyThem}</p>}
					{strategy.audience.insteadOf && (
						<p>
							<span className="font-medium">We focus here</span>{' '}
							{strategy.audience.insteadOf}.
						</p>
					)}
					<ProvChips
						provenance={strategy.audience.provenance}
						show={showProv}
					/>
				</div>
			)}
		</>
	);
}

export function MetricsSection({
	run,
	view,
	showProv,
	currentValue,
}: {
	run: Strategy;
	view: StrategyPlanView;
	showProv: boolean;
	currentValue?: number | null;
}) {
	const { definitions, glossSites, orderedMetrics } = view;

	if (orderedMetrics.length === 0) {
		return (
			<p className="text-sm text-(--text-muted)">
				No measurable metrics in this strategy yet.
			</p>
		);
	}

	return (
		<>
			<div className="flex flex-col gap-4">
				{orderedMetrics.map(({ metric: m, variant, rawIndex }) => (
					<MetricCard
						key={m.id || `metric-${rawIndex}`}
						m={m}
						variant={variant}
						showProv={showProv}
						metricIndex={rawIndex}
						definitions={definitions}
						glossSites={glossSites}
						currentValue={currentValue}
					/>
				))}
			</div>

			{showProv && (run.research?.length ?? 0) > 0 && (
				<div className="glass-card mt-4 space-y-1 p-5">
					<h2 className="text-lg font-semibold text-(--text-primary)">
						Sources &amp; examples we drew on
					</h2>
					{run.research!.map((r) => (
						<p key={r.url} className="text-sm">
							<a
								className="text-beacon-accent underline"
								href={r.url}
								target="_blank"
								rel="noreferrer"
							>
								{r.title ?? r.url}
							</a>
						</p>
					))}
				</div>
			)}
		</>
	);
}
